import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import {
  isDeviceTargetValid,
  isABWTargetValid,
  isCheckinDayTargetValid,
  isLengthOfStayTargetValid,
  isCheckinDateTargetValid,
  isUserSetDatesTargetValid,
  isLocationValid,
  notifyTargetsShowError,
  convertIntentOption,
  compareAndProcessLocations,
  physicalIntentOptions
} from '@bingads-webui-campaign/hotel-common-targets';
import {
  isAdFormatValid,
  bidStrategyOptionKeys,
  collectCriterions,
  getAudienceAds,
  isAudienceAdsValid
} from '@bingads-webui-campaign/hotel-common-upsert';
import {
  convertDeviceStateToDeviceSchema,
  convertABWStateToABWSchema,
  convertCheckInDayStateToCheckInDaySchema,
  convertLengthofStayStateToLengthofStaySchema,
  convertCheckInDateStateToCheckInDateSchema,
  convertUserSetDatesStateToUserSetDatesSchema
} from './get-targets-criterion';
import { fieldsToCheck } from './components';
import { isBaseBidValid } from './components/base-bid';
import { isAdgroupNameValid } from './components/adgroup-name';

export function getUnSupportedBidAdjustmentFeatures(CampaignBidStrategyType) {
  switch (CampaignBidStrategyType) {
    case 'Commission':
      return ['location', 'adSchedule', 'device', 'ABW', 'checkinDay', 'lengthOfStay', 'checkinDate', 'userSetDates'];
    default:
      return ['location', 'adSchedule'];
  }
}

export function isUnSupported(feature, unSupportedFeatures, callback) {
  if (unSupportedFeatures.includes(feature)) {
    return true;
  }
  return callback();
}

function getAdSchedule(adgroup, campaign) {
  const targets = adgroup.adScheduleData;
  return targets.map((criterion) => {
    if (adgroup.Id) {
      return {
        ...criterion,
        AdGroup: {
          Id: adgroup.Id,
        },
      };
    }
    return {
      ...criterion,
      CampaignName: campaign.Name,
      AdGroupName: adgroup.name.value,
    };
  });
}

function setParent(targets, adgroup, campaign) {
  return targets.map((criterion) => {
    if (adgroup.Id) {
      return {
        ...criterion,
        AdGroup: {
          Id: adgroup.Id,
        },
      };
    }
    return {
      ...criterion,
      CampaignName: campaign.Name,
      AdGroupName: adgroup.name.value,
    };
  });
}

function convertLocationStateToLocationSchema(location, adgroup, campaign) {
  const {
    useParentLevel,
    currentLevelSettings,
  } = location;

  const {
    currentLocations,
    initLocations,
  } = currentLevelSettings;

  const noLocations = useParentLevel === true || (currentLocations == null || currentLocations.length === 0);
  let finalLocations = [];
  const intent = {
    Id: location.currentLevelSettings.intentId,
    CriterionType: 'LocationIntentCriterion',
    Criterion: {
      Type: 'LocationIntentCriterion',
      LocationIntentCriterion: {
        IntentOption: convertIntentOption(location.currentLevelSettings),
      },
    },
  };

  if (noLocations) {
    finalLocations = [];
    intent.Criterion.LocationIntentCriterion.IntentOption = physicalIntentOptions.PeopleInOrSearchingForOrViewingPages;
  } else {
    finalLocations = currentLocations;
  }

  finalLocations = compareAndProcessLocations(initLocations, finalLocations);

  finalLocations = finalLocations.concat([intent]);

  finalLocations = setParent(finalLocations, adgroup, campaign);

  return finalLocations;
}


function getHotelAdGroupType(adgroup) {
  const HotelAdGroupType = [];
  const { adFormat } = adgroup;
  if (adFormat.HPA) {
    HotelAdGroupType.push('HotelAd');
  }
  if (adFormat.PPA) {
    HotelAdGroupType.push('PropertyAd');
  }
  return HotelAdGroupType;
}

export function getAdGroupBid(baseBid, bidStrategyType) {
  switch (bidStrategyType) {
    case bidStrategyOptionKeys.ManualCpc:
      return {
        SearchBid: baseBid.searchBid && decimal.fromLocalToFloat(baseBid.searchBid),
      };
    case bidStrategyOptionKeys.PercentCpc:
      return {
        AdGroupPercentCpcBid: baseBid.percentCpcBid && decimal.fromLocalToFloat(baseBid.percentCpcBid),
      };
    case bidStrategyOptionKeys.Commission:
      return {
        AdGroupCommissionRate: baseBid.commissionRate && decimal.fromLocalToFloat(baseBid.commissionRate),
      };
    default:
      return {
        SearchBid: baseBid.searchBid && decimal.fromLocalToFloat(baseBid.searchBid),
      };
  }
}

function getAdScheduleUseSearcherTimeZone(adgroup) {
  const { AdScheduleUseSearcherTimeZone } = adgroup;
  return { AdScheduleUseSearcherTimeZone };
}

export function convertAdgroupStateToAdgroupJSONSchemaData(adgroup, campaign, initTargets) {
  const {
    device: initDevice, ABW: initABW, checkinDay: initCheckinDay, lengthOfStay: initLengthOfStay, checkinDate: initCheckinDate, userSetDates: initUserSetDates,
  } = initTargets;
  const adScheduleUseSearcherTimeZone = getAdScheduleUseSearcherTimeZone(adgroup);
  const deviceSettings = convertDeviceStateToDeviceSchema(adgroup, campaign, initDevice);
  const ABWSettings = convertABWStateToABWSchema(adgroup, campaign, initABW);
  const checkinDaySettings = convertCheckInDayStateToCheckInDaySchema(adgroup, campaign, initCheckinDay);
  const lengthOfStaySettings = convertLengthofStayStateToLengthofStaySchema(adgroup, campaign, initLengthOfStay);
  const checkinDateSettings = convertCheckInDateStateToCheckInDateSchema(adgroup, campaign, initCheckinDate);
  const userSetDatesSettings = convertUserSetDatesStateToUserSetDatesSchema(adgroup, campaign, initUserSetDates);
  const locationTargets = convertLocationStateToLocationSchema(adgroup.location, adgroup, campaign);
  const adschedules = getAdSchedule(adgroup, campaign);
  const audienceAds = getAudienceAds(adgroup.audienceAds);
  const unSupportedFeatures = getUnSupportedBidAdjustmentFeatures(campaign.BiddingScheme.Type);

  // could reference mt-entity-tapi or client-data package
  const jsonSchemaData = {
    Name: adgroup.name.value,
    ...adScheduleUseSearcherTimeZone,
    ...audienceAds,
    Campaign: { Id: campaign.Id },
    CampaignName: campaign.Name,
    BiddingScheme: {
      Type: 'InheritFromParent',
    },
    ...getAdGroupBid(adgroup.baseBid, campaign.BiddingScheme.Type),
    AdGroupType: 'HotelAds',
    HotelAdGroupType: getHotelAdGroupType(adgroup),
    ...adgroup.dateSettings,
    ...adgroup.netWorkOptions,
    TrackingUrlTemplate: adgroup.urlOptions.TrackingUrlTemplate,
    FinalUrlSuffix: adgroup.urlOptions.FinalUrlSuffix,
    UrlCustomParameters: adgroup.urlOptions.UrlCustomParameters,
    AdGroupCriterions: [ // new targetings waiting for api
      ...collectCriterions('adSchedule', unSupportedFeatures, adschedules),
      ...collectCriterions('device', unSupportedFeatures, deviceSettings),
      ...collectCriterions('ABW', unSupportedFeatures, ABWSettings),
      ...collectCriterions('checkinDay', unSupportedFeatures, checkinDaySettings),
      ...collectCriterions('lengthOfStay', unSupportedFeatures, lengthOfStaySettings),
      ...collectCriterions('checkinDate', unSupportedFeatures, checkinDateSettings),
      ...collectCriterions('userSetDates', unSupportedFeatures, userSetDatesSettings),
      ...collectCriterions('location', unSupportedFeatures, locationTargets),
    ],
  };

  if (adgroup.Id) {
    return {
      Id: adgroup.Id,
      ...jsonSchemaData,
    };
  }
  return {
    Status: 'Active',
    ...jsonSchemaData,
  };
}

export function validateAdGroupState(adgroup, serverContext, bidStrategyType, patchAdgroup, patchErrMsg) {
  notifyTargetsShowError(adgroup, patchAdgroup);

  const unSupportedFeatures = getUnSupportedBidAdjustmentFeatures(bidStrategyType);
  return {
    name: isAdgroupNameValid(adgroup.name.value),
    adFormat: isAdFormatValid(adgroup.adFormat),
    baseBid: isBaseBidValid({ baseBid: adgroup.baseBid, bidStrategyType, serverContext }),
    audienceAds: isAudienceAdsValid(adgroup.audienceAds),
    device: isUnSupported('device', unSupportedFeatures, () => (adgroup.device.useParentLevel ? true : isDeviceTargetValid(adgroup.device.currentLevelSettings, patchErrMsg))),
    ABW: isUnSupported('ABW', unSupportedFeatures, () => (adgroup.ABW.useParentLevel ? true : isABWTargetValid(adgroup.ABW.currentLevelSettings, patchErrMsg))),
    checkinDay: isUnSupported('checkinDay', unSupportedFeatures, () => (adgroup.checkinDay.useParentLevel ? true : isCheckinDayTargetValid(adgroup.checkinDay.currentLevelSettings, patchErrMsg))),
    lengthOfStay: isUnSupported('lengthOfStay', unSupportedFeatures, () => (adgroup.lengthOfStay.useParentLevel ? true : isLengthOfStayTargetValid(adgroup.lengthOfStay.currentLevelSettings, patchErrMsg))),
    checkinDate: isUnSupported('checkinDate', unSupportedFeatures, () => (adgroup.checkinDate.useParentLevel ? true : isCheckinDateTargetValid(adgroup.checkinDate.currentLevelSettings, patchErrMsg))),
    userSetDates: isUnSupported('userSetDates', unSupportedFeatures, () => (adgroup.userSetDates.useParentLevel ? true : isUserSetDatesTargetValid(adgroup.userSetDates.currentLevelSettings, patchErrMsg))),
    location: isUnSupported('location', unSupportedFeatures, () => isLocationValid(adgroup.location)),
  };
}

export function generateDataToInvalidState(validationData, adgroup) {
  let data = { ...adgroup };
  fieldsToCheck.forEach((field) => {
    if (validationData[field] === false) {
      data = {
        ...data,
        [field]: {
          ...data[field],
          isInvalid: true,
        },
      };
    }
  });
  return data;
}
